import { useCallback, useEffect, useState } from 'react';

import { PageTitle } from '@components/PageTitle';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Separator } from '@components/ui/separator';
import { useAuth } from '@contexts/auth';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import BigNumber from 'bignumber.js';
import { useForm } from 'react-hook-form';
import { NumberFormatBase } from 'react-number-format';
import { Navigate } from 'react-router';
import { z } from 'zod';

import { useConversorCorticoide } from './context';

interface IEquivalence {
  duration: string;
  mg: number;
  name: string;
}

const equivalences: IEquivalence[] = [
  {
    name: 'Prednisona',
    mg: 1,
    duration: '24 a 36 horas',
  },
  {
    name: 'Prednisolona',
    mg: 1,
    duration: '24 a 36 horas',
  },
  {
    name: 'Dexametasona',
    mg: 0.15,
    duration: '36 a 54 horas',
  },
  {
    name: 'Hidrocortisona',
    mg: 4,
    duration: '8 a 12 horas',
  },
  {
    name: 'Metilprednisolona',
    mg: 0.8,
    duration: '24 a 36 horas',
  },
  {
    name: 'Betametasona',
    mg: 0.15,
    duration: '36 a 54 horas',
  },
  {
    name: 'Cortisona',
    mg: 5,
    duration: '8 a 12 horas',
  },
  {
    name: 'Triancinolona',
    mg: 0.8,
    duration: '24 a 36 horas',
  },
];

const options = [
  {
    label: 'Betametasona',
    value: 'Betametasona',
  },
  {
    label: 'Cortisona',
    value: 'Cortisona',
  },
  {
    label: 'Dexametasona',
    value: 'Dexametasona',
  },
  {
    label: 'Hidrocortisona',
    value: 'Hidrocortisona',
  },
  {
    label: 'Metilprednisolona',
    value: 'Metilprednisolona',
  },
  {
    label: 'Prednisolona',
    value: 'Prednisolona',
  },
  {
    label: 'Prednisona',
    value: 'Prednisona',
  },
  {
    label: 'Triancinolona',
    value: 'Triancinolona',
  },
];

const formSchema = z.object({
  drugOne: z.string().min(1, {
    message: errorsValidation.required,
  }),
  drugTwo: z.string().min(1, {
    message: errorsValidation.required,
  }),
  mg: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function CorticosteroidConverter() {
  const { user } = useAuth();
  const [equivalenceCalculated, setEquivalenceCalculated] = useState<number>(0);
  const [durationDrugOne, setDurationDrugOne] = useState<string>('');
  const [durationDrugTwo, setDurationDrugTwo] = useState<string>('');

  const { drugOne, drugTwo, mg, setDrugOne, setDrugTwo, setMg } =
    useConversorCorticoide();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      drugOne: 'Prednisolona',
      drugTwo: 'Dexametasona',
      mg: mg.toFixed(1),
    },
  });

  const handleChangeDrugOne = useCallback(
    (value: string): void => {
      setDrugOne(value);
    },
    [setDrugOne],
  );

  const handleChangeDrugTwo = useCallback(
    (value: string): void => {
      setDrugTwo(value);
    },
    [setDrugTwo],
  );

  const handleChangeMg = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setMg(valueParsed);
    },
    [setMg],
  );

  const calculateEquivalence = useCallback((): void => {
    // FÓRMULA: mg * (mg destino / mg origem)
    const equivalenceDrugOne = equivalences.find(
      (item) => item.name === drugOne,
    );
    if (!equivalenceDrugOne) {
      return;
    }
    setDurationDrugOne(equivalenceDrugOne.duration);
    const equivalenceDrugTwo = equivalences.find(
      (item) => item.name === drugTwo,
    );
    if (!equivalenceDrugTwo) {
      return;
    }
    setDurationDrugTwo(equivalenceDrugTwo.duration);
    const mgParsed = new BigNumber(mg);
    const stepOne = new BigNumber(equivalenceDrugTwo.mg).dividedBy(
      equivalenceDrugOne.mg,
    );
    const result = stepOne.multipliedBy(mgParsed);
    setEquivalenceCalculated(result.toNumber());
  }, [drugOne, drugTwo, mg]);

  useEffect(() => {
    calculateEquivalence();
  }, [calculateEquivalence]);

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'drugOne' && value.drugOne) {
        handleChangeDrugOne(value.drugOne);
      }
      if (name === 'drugTwo' && value.drugTwo) {
        handleChangeDrugTwo(value.drugTwo);
      }
      if (name === 'mg' && value.mg) {
        handleChangeMg(value.mg);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  if (!user?.accessGranted) {
    return <Navigate replace to="/pagamento" />;
  }

  return (
    <div>
      <PageTitle hasBackButton title="Conversor corticoide" />
      <Form {...form}>
        <form
          className="mx-auto max-w-3xl"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="drugOne"
              render={({ field }) => (
                <FormItem className="grid w-full max-w-sm grid-cols-[2.5rem_1fr] items-center">
                  <FormLabel>De:</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger
                        className="h-fit text-start"
                        onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {options.map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="drugTwo"
              render={({ field }) => (
                <FormItem className="grid w-full max-w-sm grid-cols-[2.5rem_1fr] items-center">
                  <FormLabel>Para:</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger
                        className="h-fit text-start"
                        onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {options.map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="mg"
              render={({ field: { ref, ...rest } }) => (
                <FormItem className="grid w-full max-w-sm grid-cols-[2.5rem_1fr] items-center">
                  <FormLabel>mg:</FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-20 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <Separator className="my-4" />

          <div className="mx-auto mb-4 block w-fit text-center">
            <div className="mb-2 grid grid-cols-[min-content_max-content_max-content] items-center gap-2">
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {mg.toFixed(1)}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">
                mg de
              </Label>
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {drugOne}
                </span>
              </p>
            </div>

            <Label className="text-foreground text-sm font-light">
              equivale a
            </Label>

            <div className="mt-2 grid grid-cols-[min-content_max-content_max-content] items-center gap-2">
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {equivalenceCalculated.toFixed(1)}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">
                mg de
              </Label>
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {drugTwo}
                </span>
              </p>
            </div>
          </div>

          <div className="mb-4 flex flex-col items-center justify-center gap-2">
            <Label>Duração biológica</Label>
            <Label className="text-foreground text-sm font-light">{`${drugOne} ${durationDrugOne}`}</Label>
            <Label className="text-foreground text-sm font-light">{`${drugTwo} ${durationDrugTwo}`}</Label>
          </div>

          <p className="text-foreground mt-4 text-center text-xs font-light">
            Referência: Cardozo Pereira AL et al. Uso sistêmico de
            corticosteróides: revisão da literatura. Med Cutan Iber Lat Am
            2007;35(1):35-50 .
          </p>
        </form>
      </Form>
    </div>
  );
}

export default CorticosteroidConverter;
